import { zonedTimeToUtc } from "date-fns-tz";

import type { RiskPictureItem } from "./risk-picture-model";

const DEFAULT_TIME_ZONE = "Europe/Amsterdam";

/**
 * Whether to show the countdown (connector + label). Hidden for imminent warnings, and when
 * the label would not leave 4px between text and each endpoint.
 */
export const riskPictureCountdownFits = (
  minutes: number,
  gapWidth: number,
  labelWidth: number,
): boolean => minutes >= 15 && labelWidth > 0 && gapWidth >= labelWidth + 8;

/** Local hours with an axis label. 24 is the next midnight, drawn at the right edge. */
const TICK_HOURS = [0, 3, 6, 9, 12, 15, 18, 21, 24] as const;

export interface RiskPictureTimelineSegment {
  item: RiskPictureItem;
  /** Fraction of the day, 0–1, after clipping to the day. */
  offset: number;
  width: number;
  /**
   * Connector to an upcoming onset, only on the day containing now. Starts at now, or at the
   * end of the lane's previous bar if that is still running. `minutes` always counts from now.
   */
  countdown?: { offset: number; width: number; minutes: number };
}

export interface RiskPictureTimelineRow {
  phenomenon: string;
  /** Each lane holds segments that do not overlap; overlapping warnings get their own lane. */
  lanes: RiskPictureTimelineSegment[][];
}

export interface RiskPictureTimelineTick {
  hour: number;
  offset: number;
}

export interface RiskPictureTimeline {
  rows: RiskPictureTimelineRow[];
  ticks: RiskPictureTimelineTick[];
  /** Set only when `now` falls inside the day. */
  nowOffset?: number;
}

interface BuildRiskPictureTimelineOptions {
  now?: Date;
  timeZone?: string;
  /** Table order. Rows are still only phenomena that have a warning this day. */
  phenomena?: readonly string[];
}

interface Placed {
  item: RiskPictureItem;
  start: number;
  end: number;
}

const localTime = (date: string, hour: number, timeZone: string): number =>
  zonedTimeToUtc(`${date}T${String(hour).padStart(2, "0")}:00:00`, timeZone).getTime();

const nextDate = (date: string): string => {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10);
};

const phenomenonOrder = (present: string[], catalog: readonly string[]): string[] => {
  if (catalog.length === 0) return present;
  return catalog.filter((phenomenon) => present.includes(phenomenon));
};

/** Greedy first-fit: a segment joins the first lane whose last segment ended before it starts. */
const assignLanes = (placed: Placed[]): Placed[][] => {
  const sorted = [...placed].sort(
    (a, b) => a.start - b.start || a.end - b.end || a.item.id.localeCompare(b.item.id),
  );
  const lanes: Placed[][] = [];
  for (const segment of sorted) {
    const lane = lanes.find(
      (candidate) => candidate[candidate.length - 1].end <= segment.start,
    );
    if (lane) lane.push(segment);
    else lanes.push([segment]);
  }
  return lanes;
};

/**
 * Lays out one local calendar day of warnings on a 00:00–24:00 axis.
 *
 * Rows follow `phenomena` and only include phenomena with warnings. Parts are
 * clipped to the day, so a warning over midnight starts at 0
 * or ends at 1. Positions are fractions of the real day length, which is 23 or
 * 25 hours on DST changes. Items without valid onset/expires are left out.
 */
export const buildRiskPictureTimeline = (
  items: RiskPictureItem[],
  date: string,
  {
    now,
    timeZone = DEFAULT_TIME_ZONE,
    phenomena: catalog = [],
  }: BuildRiskPictureTimelineOptions = {},
): RiskPictureTimeline => {
  const dayStart = localTime(date, 0, timeZone);
  const dayEnd = localTime(nextDate(date), 0, timeZone);
  const length = dayEnd - dayStart;
  const fraction = (time: number) => (time - dayStart) / length;
  const nowTime = now?.getTime();
  const nowOffset =
    nowTime !== undefined && nowTime >= dayStart && nowTime < dayEnd
      ? fraction(nowTime)
      : undefined;

  const placed = items.flatMap((item): Placed[] => {
    const onset = item.onset ? Date.parse(item.onset) : Number.NaN;
    const expires = item.expires ? Date.parse(item.expires) : Number.NaN;
    if (Number.isNaN(onset) || Number.isNaN(expires)) return [];
    const start = Math.max(onset, dayStart);
    const end = Math.min(expires, dayEnd);
    return end > start ? [{ item, start, end }] : [];
  });

  const phenomena = phenomenonOrder(
    [...new Set(placed.map(({ item }) => item.phenomenon))],
    catalog,
  );

  const rows = phenomena.map((phenomenon): RiskPictureTimelineRow => ({
    phenomenon,
    lanes: assignLanes(placed.filter(({ item }) => item.phenomenon === phenomenon)).map(
      (lane) =>
        lane.map(({ item, start, end }, index) => {
          // Lanes never overlap, so a still-running predecessor ends before this onset.
          // Start the connector there instead of at now, so it never covers that bar.
          const from =
            nowTime === undefined
              ? undefined
              : Math.max(nowTime, lane[index - 1]?.end ?? 0);
          return {
            item,
            offset: fraction(start),
            width: fraction(end) - fraction(start),
            countdown:
              nowOffset !== undefined &&
              nowTime !== undefined &&
              from !== undefined &&
              start > nowTime
                ? {
                    offset: fraction(from),
                    width: fraction(start) - fraction(from),
                    minutes: Math.ceil((start - nowTime) / 60_000),
                  }
                : undefined,
          };
        }),
    ),
  }));

  const ticks = TICK_HOURS.map((hour) => ({
    hour,
    offset: hour === 24 ? 1 : fraction(localTime(date, hour, timeZone)),
  }));

  return { rows, ticks, nowOffset };
};

/**
 * Pixel height of `.timelineChart` for these rows.
 *
 * Mirrors the axis and current-time footer (16 each), `.timelineChart` gap (8),
 * `.timelineLane` (24), and `.timelineLanes > .timelineScale` gap (4)
 * plus `.timelineLanes` vertical padding (4).
 * The band reserves the max across days so a quieter day does not shrink the
 * panel and slide the next-day button out from under the cursor.
 */
const TIMELINE_AXIS_HEIGHT = 16;
const TIMELINE_FOOTER_HEIGHT = 16;
const TIMELINE_CHART_GAP = 8;
const TIMELINE_LANE_HEIGHT = 24;
const TIMELINE_LANE_GAP = 4;
const TIMELINE_LANES_PADDING = 4;

export const riskPictureTimelineChartHeight = (
  rows: readonly { lanes: readonly (readonly { countdown?: unknown }[])[] }[],
): number => {
  if (rows.length === 0) return 0;

  const rowsHeight = rows.reduce((sum, row) => {
    const lanes = Math.max(row.lanes.length, 1);
    return (
      sum +
      lanes * TIMELINE_LANE_HEIGHT +
      Math.max(lanes - 1, 0) * TIMELINE_LANE_GAP +
      TIMELINE_LANES_PADDING
    );
  }, 0);

  return (
    TIMELINE_AXIS_HEIGHT +
    TIMELINE_FOOTER_HEIGHT +
    (rows.length + 1) * TIMELINE_CHART_GAP +
    rowsHeight
  );
};

/** Same ceiling as the inline band (`.timeline` max-height). */
const DRAWER_MAX_HEIGHT = 320;
/** Share of the map the open drawer may cover, so map and card toolbar stay usable. */
const DRAWER_MAP_SHARE = 0.6;

/**
 * Max height of the fullscreen timeline drawer, in px.
 *
 * The drawer opens over the map, so on a short phone a fixed 320px would cover the whole
 * map and the card toolbar (exit fullscreen) above it. Capped at 60% of the map instead.
 * Unmeasured (0 or less) falls back to the plain ceiling.
 */
export const riskPictureDrawerMaxHeight = (mapHeight: number): number => {
  if (!(mapHeight > 0)) return DRAWER_MAX_HEIGHT;
  return Math.min(DRAWER_MAX_HEIGHT, Math.floor(mapHeight * DRAWER_MAP_SHARE));
};
